import React, { useEffect, useMemo, useRef } from 'react';
import { ComponentProps } from 'types';
import storage from 'lib/utils/storage';
import { STORAGE } from 'lib/utils/storage/constants';
import { useUserOrderStore } from 'lib/store/userOrderStore';
import { ORDER_STATE } from 'lib/constants/order';
import { useMobileStore } from 'lib/store/mobileStore';
import { validMobile } from 'lib/utils/util';
import { ATTA_GATEWAY_MOBILE, ATTA_REPORT } from 'lib/constants/atta';
import { report } from 'lib/utils/dataReport';
import { gateway } from 'lib/utils/authMobile/gatewayMobile';
import msdkUtil from 'lib/utils/MSDK';
import { useFinishInit } from 'lib/hooks/useFinishInit';
import { getIcAuthMobile } from 'lib/utils/authMobile/icMobile';
import { isPCAdapterActive } from 'lib/utils/pcAdapter';
import './index.scss';

interface MobileInputProps {
  text: string;
  enableICAuthMobile: boolean;
  enableGatewayMobile: boolean;
  channelId: string;
  enableJsBridge: boolean;
}

export default function MobileInput({ data, commonStyle, style }: ComponentProps<MobileInputProps>) {
  const { text, enableICAuthMobile, enableGatewayMobile, channelId, enableJsBridge } = data;
  const { textAlign, textColor, fontSize } = style;
  const userOrderInfo = useUserOrderStore((state) => state.userOrderInfo);
  const queryMobileOrderStatus = useMobileStore((state) => state.queryMobileOrderStatus);
  const updateInputMobile = useMobileStore((state) => state.updateInputMobile);
  const mobileInput = useMobileStore((state) => state.inputMobile);
  const updateGatewayMobile = useMobileStore((state) => state.updateGatewayMobile);
  const mobile = useMemo(() => userOrderInfo?.orderMobile || storage.getStorage(STORAGE.ORDER_MOBILE), [userOrderInfo]);
  const inputRef = useRef<HTMLInputElement>(null);
  const authMobileInitFlag = useRef<boolean>(false);

  // 字体大小小于8px时不生效,避免错配
  const customizeStyle = fontSize >= 8 ? { fontSize } : {};

  const disabledInput = useMemo(
    () =>
      [ORDER_STATE.ORDER_SUCCESS, ORDER_STATE.CANCELING, ORDER_STATE.CANCEL_SUCCESS, ORDER_STATE.ORDERING].includes(
        userOrderInfo.orderState,
      ),
    [userOrderInfo],
  );

  useEffect(() => {
    if (isPCAdapterActive()) return;

    const handleOrientationChange = () => {
      if (document?.activeElement?.tagName === 'INPUT') {
        document.activeElement?.blur();
      }
    };

    const handleScreenOrientationChange = () => {
      handleOrientationChange();
    };

    const handleWindowOrientationChange = () => {
      setTimeout(() => {
        handleOrientationChange();
      }, 100);
    };

    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleScreenOrientationChange);
    } else {
      window.addEventListener('orientationchange', handleWindowOrientationChange);
    }

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleScreenOrientationChange);
      } else {
        window.removeEventListener('orientationchange', handleWindowOrientationChange);
      }
    };
  }, []);

  useEffect(() => {
    if (isPCAdapterActive()) return;

    const handler = () => {
      if (document?.activeElement?.tagName === 'INPUT' || document?.activeElement?.tagName === 'TEXTAREA') {
        document.activeElement?.blur();
      }
    };
    window.addEventListener('touchmove', handler);
    return () => {
      window.removeEventListener('touchmove', handler);
    };
  }, []);

  useFinishInit(() => {
    setTimeout(() => {
      if (!mobileInput && !mobile) return;
      // 存在已订手机号或已输入手机号则将网关手机号置为空
      updateGatewayMobile('', false);
    }, 0);
  });

  useFinishInit(() => {
    const getICMobile = async () => {
      if (mobileInput || mobile || !msdkUtil.isMSDKEnv || !enableICAuthMobile) {
        return;
      }
      const icMobile = await getIcAuthMobile();
      report(ATTA_GATEWAY_MOBILE.IC_MOBILE_RESULT, {
        eventResult: !!icMobile ? 0 : -1,
      });
      if (!validMobile(icMobile)) return;
      updateInputMobile(icMobile);
      return;
    };
    getICMobile();
  });

  useEffect(() => {
    // 存在已订手机号或已输入手机号则不再获取
    if (authMobileInitFlag.current || mobileInput || mobile) {
      authMobileInitFlag.current = true;
      return;
    }

    const getGatewayMobile = async () => {
      authMobileInitFlag.current = true;
      if (!channelId || !enableGatewayMobile) {
        return;
      }
      gateway.initGatewayMobile({ channelId, enableJsBridge });
      const maskMobile = await gateway.getMaskMobile();
      if (!maskMobile) return;
      updateGatewayMobile(maskMobile);
      return;
    };
    getGatewayMobile();
  }, [enableJsBridge, channelId, enableGatewayMobile, mobile, mobileInput]);

  useEffect(() => {
    // 如果存在订手机号则更新手机号输入框
    if (mobile) {
      updateInputMobile(mobile);
      // 存在登录态且未订购且输入过手机号
      userOrderInfo.orderState === ORDER_STATE.NOT_ORDER && queryMobileOrderStatus(mobile);
    }
  }, [mobile, userOrderInfo]);

  const inputMobile = (e: any) => {
    const inputValue = e.target.value.replace(/\D/g, '');
    updateInputMobile(inputValue);
    if (validMobile(inputValue)) {
      report(ATTA_REPORT.ORDER.INPUT_FINISH, {
        mobile: inputValue,
      });
    }
  };

  // 清理网关手机号
  const reset = () => {
    report(ATTA_GATEWAY_MOBILE.RESET_CLICK);
    updateGatewayMobile('', false);
  };

  // focus时将input滚动到可视区域
  const focusInput = () => {
    reset();
    if (!isPCAdapterActive()) {
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ block: 'center' });
      }, 0);
    }
  };

  const inputValueToShow = useMobileStore((state) => state.getInputValueToShow());

  return (
    <div style={commonStyle} className="mobile-input">
      <input
        ref={inputRef}
        className="mobile-input__input"
        disabled={disabledInput}
        // tel 并不能限制输入非数字，所以需要使用 inputMode 限制输入
        type="tel"
        inputMode="tel"
        id="mobileInput"
        placeholder={text}
        value={inputValueToShow}
        onChange={inputMobile}
        onFocus={focusInput}
        maxLength={11}
        // eslint-disable-next-line
        style={{
          textAlign,
          color: textColor,
          '--placeholder-color': textColor,
          ...customizeStyle,
        }}
      />
    </div>
  );
}
